<template>
  <div ref="root" class="relative shrink-0" data-testid="experience-sort-select">
    <span :id="labelId" class="sr-only">Sort</span>
    <button
      :id="triggerId"
      aria-haspopup="listbox"
      class="flex cursor-pointer items-center gap-2 font-Spectral text-base font-light leading-tight text-stone-500 hover:text-gray4"
      type="button"
      :aria-expanded="isOpen"
      :aria-labelledby="`${labelId} ${triggerId}`"
      @click="toggle"
      @keydown.esc="close"
    >
      <svg aria-hidden="true" class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" stroke-width="1" viewBox="0 0 16 16">
        <path d="M5 13V3m0 0L2.5 5.5M5 3l2.5 2.5" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M11 3v10m0 0l2.5-2.5M11 13l-2.5-2.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      {{ selectedLabel }}
    </button>
    <div
      v-show="isOpen"
      class="absolute right-0 top-full z-10 mt-2 min-w-[160px] overflow-hidden whitespace-nowrap rounded-sm border-[0.5px] border-beige3 bg-white"
      role="listbox"
      :aria-labelledby="labelId"
    >
      <button
        v-for="option in options"
        :key="option.value"
        role="option"
        type="button"
        :aria-selected="option.value === modelValue"
        :class="['block w-full px-4 py-2 text-left font-Spectral text-base font-light hover:bg-beige1', option.value === modelValue ? 'text-gray9' : 'text-gray4']"
        @click="select(option.value)"
        @keydown.esc="close"
      >
        {{ option.label }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';

import { EXPERIENCE_SORT_TYPES, type ExperienceSortType } from '@/wabunka-src/common/features/Experience/utils/experienceListSort';

interface Props {
  modelValue: ExperienceSortType;
  onChange: (sortType: ExperienceSortType) => void;
}
const props = defineProps<Props>();

const labelId = 'experience-list-sort-label';
const triggerId = 'experience-list-sort';

const options: { value: ExperienceSortType; label: string }[] = [
  { value: EXPERIENCE_SORT_TYPES.recommended, label: 'Recommended' },
  { value: EXPERIENCE_SORT_TYPES.newest, label: 'Newest' },
];

const selectedLabel = computed<string>(() => options.find((option) => option.value === props.modelValue)?.label ?? '');

const isOpen = ref<boolean>(false);
const toggle = () => {
  isOpen.value = !isOpen.value;
};
const close = () => {
  isOpen.value = false;
};

const select = (sortType: ExperienceSortType) => {
  close();
  if (sortType !== props.modelValue) props.onChange(sortType);
};

const root = ref<HTMLElement | null>(null);
onClickOutside(root, close);
</script>
